/**
 * Modale « Note d'équipe » au CLIC sur la note d'une équipe (classement Elo) : la
 * liste des joueurs de l'équipe avec leur NOTE MOYENNE de compétition (sur tous
 * leurs matchs de Coupe du monde) et, en petit, le nombre de matchs joués. Triés
 * par note décroissante. Le nom ouvre la fiche joueur (courbe + détail par match).
 *
 * Données agrégées côté serveur depuis l'archive (`/data/player-ratings/<CODE>.json`,
 * cf. server/leaderboards.mjs) — donc disponibles seulement quand des matchs ont été
 * joués/archivés (pas de graine committée : avant le tournoi, rien à montrer ; échec
 * réseau ⇒ message discret, l'app reste intacte — règle 8). Réutilise `.md-overlay`.
 */
import { teamName, flagCode, esc, frontZ, playerLink } from "./html.ts";
import { track } from "../app/track.ts";
import { t } from "../app/i18n.ts";

export interface PlayerRatingRow {
  readonly id: number;
  readonly name: string | null;
  readonly position: string | null;
  readonly rating: number;
  readonly apps: number; // matchs joués (minutes > 0)
  readonly ratingN: number; // matchs effectivement notés
}
export interface PlayerRatingsDoc {
  readonly code: string;
  readonly generatedAt?: string | null;
  readonly players: readonly PlayerRatingRow[];
}

const POS_KEY: Record<string, string> = {
  Goalkeeper: "fiche.player.pos.goalkeeper",
  Defender: "fiche.player.pos.defender",
  Midfielder: "fiche.player.pos.midfielder",
  Attacker: "fiche.player.pos.attacker",
};
const posLabel = (p: string | null): string => {
  if (!p) return "";
  const key = POS_KEY[p];
  return key ? t(key) : p;
};
const matchWord = (n: number): string => `${n} ${t("common.match")}${n > 1 ? "s" : ""}`;

/** Corps de la modale (PUR → testé) : en-tête équipe + liste des joueurs notés. */
export function ratingsBodyHtml(code: string, doc: PlayerRatingsDoc | null): string {
  const list = doc?.players ?? [];
  const head = `<div class="sv-head">
      <span class="f fi fi-${esc(flagCode(code))} sv-flag" aria-hidden="true"></span>
      <div class="sv-id">
        <div class="sv-name">${esc(teamName(code))}</div>
        <div class="sv-sub">${t("fiche.playerRatings.subtitle")} · <b>${list.length}</b> ${t("common.player")}${list.length > 1 ? "s" : ""} ${t("common.rated")}${list.length > 1 ? "s" : ""}</div>
      </div>
    </div>`;
  if (!list.length) {
    return `${head}<p class="sv-empty note">${t("fiche.playerRatings.empty")}</p>`;
  }
  const rows = list
    .map((p, i) => {
      const meta = posLabel(p.position); // le nombre de matchs est rendu en petit près de la note
      // moyenne sur N matchs notés (≤ matchs joués si une note manque encore)
      const noteTitle =
        p.ratingN && p.ratingN !== p.apps
          ? t("fiche.playerRatings.avgOnRated", {
              n: `${matchWord(p.ratingN)} ${t("common.rated")}${p.ratingN > 1 ? "s" : ""}`,
            })
          : t("fiche.playerRatings.avgOn", { n: matchWord(p.apps) });
      return `<li class="pr-row">
      <span class="sv-rank">${i + 1}</span>
      <div class="sv-pl">
        <span class="sv-pl-name">${playerLink(p.name ?? "—", p.id)}</span>
        ${meta ? `<span class="sv-pl-meta">${esc(meta)}</span>` : ""}
      </div>
      <span class="pr-note" title="${esc(noteTitle)}"><b>${p.rating.toFixed(1)}</b><small>${esc(matchWord(p.apps))}</small></span>
    </li>`;
    })
    .join("");
  return `${head}
    <ol class="sv-list">${rows}</ol>
    <p class="note sv-foot">Note moyenne sur les matchs de Coupe du monde (notes par match API-Football) · triées par note décroissante · clique un nom pour la courbe match par match.</p>`;
}

// --- données (fetch + cache par code, prod uniquement) ----------------------
const cache = new Map<string, PlayerRatingsDoc | null>();
const pending = new Map<string, Promise<PlayerRatingsDoc | null>>();
function fetchRatings(code: string): Promise<PlayerRatingsDoc | null> {
  if (cache.has(code)) return Promise.resolve(cache.get(code) ?? null);
  const inflight = pending.get(code);
  if (inflight) return inflight;
  const p = fetch(`data/player-ratings/${code}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<PlayerRatingsDoc>) : null))
    .then((doc) => {
      const val = doc && Array.isArray(doc.players) ? doc : null;
      cache.set(code, val);
      return val;
    })
    .catch(() => {
      cache.set(code, null);
      return null;
    })
    .finally(() => pending.delete(code));
  pending.set(code, p);
  return p;
}

// --- contrôleur overlay (réutilise .md-overlay) -----------------------------
let overlay: HTMLElement | null = null;
let open = false;
let installed = false;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const ov = document.createElement("div");
  ov.className = "md-overlay sv-overlay pr-overlay hide";
  ov.innerHTML = `<div class="md-card sv-card" role="dialog" aria-modal="true" aria-label="Note d'équipe par joueur"><button type="button" class="md-close" aria-label="Fermer"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body sv-body"></div></div>`;
  ov.addEventListener("click", (e) => {
    if (e.target === ov) close();
  });
  ov.querySelector(".md-close")?.addEventListener("click", () => close());
  overlay = ov;
  document.body.appendChild(ov);
  return ov;
}

function openPlayerRatings(code: string): void {
  track("fiche", { kind: "rating", code });
  const ov = ensureOverlay();
  const body = ov.querySelector<HTMLElement>(".sv-body");
  if (!body) return;
  // Affichage immédiat (chargement ou cache), puis remplissage à l'arrivée des données.
  body.innerHTML = cache.has(code)
    ? ratingsBodyHtml(code, cache.get(code) ?? null)
    : ratingsBodyHtml(code, { code, players: [] });
  body.scrollTop = 0;
  ov.classList.remove("hide");
  ov.style.zIndex = String(frontZ());
  open = true;
  requestAnimationFrame(() => ov.classList.add("open"));
  void fetchRatings(code).then((doc) => {
    // ne re-rend que si la modale est toujours ouverte sur la même équipe
    if (open && body.isConnected) body.innerHTML = ratingsBodyHtml(code, doc);
  });
}

function close(): void {
  if (!overlay || !open) return;
  open = false;
  overlay.classList.remove("open");
  const ov = overlay;
  setTimeout(() => {
    if (!open) ov.classList.add("hide");
  }, 300);
}

/** Installe le contrôleur (idempotent) : clic sur `[data-rating-team]` → modale. */
export function registerPlayerRatings(): void {
  if (installed) return;
  installed = true;
  document.addEventListener("click", (e) => {
    const btn =
      e.target instanceof Element ? e.target.closest<HTMLElement>("[data-rating-team]") : null;
    const code = btn?.dataset.ratingTeam;
    if (!code) return;
    e.preventDefault();
    openPlayerRatings(code);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });
}
